<template>
  <div>
    <section>
      <div class="container mx-auto row middle">
        <div class="cols-1of2">
          <h2>The Science Explained</h2>

          <h3>You are unique</h3>

          <p>Everyone is different. Our DNA, the building blocks of life, determine a different path for everyone. For this reason, the nutrition needs of every person is different.</p>

          <p>My New Life brings science to you and your body, to live better for longer.</p>

          <p>The optimised nutritional needs of every person is built on two key scientific pillars:</p>

          <ul>
            <li>nutrigenomics, and</li>
            <li>nutrigenetics</li>
          </ul>

          <p>Your DNA codes your bodies ability to create and process all types of biologically active compounds necessary for health. When you don&rsquo;t get the amount of those compounds you need (too little or too much), then over time this leads to chronic medical conditions.</p>

          <p>Have you noticed that some people lack energy, are always stiff or sore, have skin problems, battle with depression or anxiety or can&rsquo;t sleep. And often, DNA so do their parents and others within their family. It&rsquo;s their DNA.</p>

          <p>Genes, the building blocks of our DNA, are being expressed.</p>

          <p><b>Nutrigenomics is the science that studies how nutrients can affect gene expression.</b></p>
        </div>

        <div class="cols-1of2">
          <h4>A Tailored Approach</h4>

          <img src="/img/science-explained.png" class="diagram" />

          <h4>Your DNA and Nutrigenomics</h4>

          <img src="/img/nutrigenomics.png" class="diagram" />
        </div>
      </div>
    </section>

    <section>
      <div class="container mx-auto row">
        <div class="cols-1of2">
          <h2>Your Lifestyle</h2>

          <p>Nutrition science tells us that people of a certain age, sex or with certain risk lifestyle habits are at risk of developing specific medical conditions in the future.</p>

          <p>For example, we know that nearly 50% of adults in modern societies are already deficient in vitamin B12, magnesium, iron, vitamin D and iodine.</p>

          <p>Why?</p>

          <p>Because the food we eat today is simply not as nutritious as it used to be. Packaged foods are heavily processed and even fresh foods, which are available in supermarkets all year round, are cultivated in a way which maximises yield but at the expense of nutrition.</p>

          <p>After 5-10 years because of micro-deficiency, symptoms begin to develop which are completely avoidable. These include:</p>

          <ul>
            <li>Fertility problems</li>
            <li>Mental health issues</li>
            <li>Cardiovascular disease</li>
            <li>Chronic inflammatory conditions &ndash; joints, organs or nerves</li>
            <li>Skin conditions</li>
          </ul>

          <p>Have you noticed, for example, how common mental health or cardiovascular conditions are today compared to 20 years ago?</p>
        </div>

        <div class="cols-1of2">
          <p>The food we eat has never been higher in calories yet so low in nutrition and micronutrients. This deficiency changes the way good and bad genes are expressed and creates disease. By targeting the right nutrient, you can prevent disease and aging.</p>

          <p>This is nutrigenetics &ndash; the study of the relationships between gene variations, diet, and health outcomes.</p>

          <p><b>Using up-to-date nutrigenetics data, we are able to supplement specific lifestages for men and women with targeted nutriceuticals to prevent chronic disease.</b></p>

          <h3>Increasing your health expectancy with My New Life</h3>

          <p>My New Life is an Australian company committed to improving the health expectancy of people all around the world. Even though life expectancy in people is growing, health expectancy (the amount of time that we live without disease) is reducing.</p>

          <p>My New Life:</p>

          <ul>
            <li>uses the science of nutrigenomics and nutrigenetics to make personalised nutriceuticals</li>
            <li>uses therapeutic doses of the correct ingredients = nutriceutical. We don&rsquo;t make vitamins.</li>
            <li>is made from nutriceutical ingredients from TGA-registered sources in Australia</li>
            <li>is made by hand. We make every personalised nutriceutical in Melbourne.</li>
          </ul>

          <p>Depending on your requirements, we will send you enough nutriceuticals to last approximately 90 days.</p>
        </div>
      </div>
    </section>

    <section>
      <div class="container mx-auto row">
        <div class="cols-1of2">
          <h2>Your Personalised Nutriceutical Program</h2>

          <div class="intro">
            <p>Nutriceuticals deliver specific combinations of biologically active food components in high dose. In contrast, a dietary supplement, or vitamin, simply supplements the diet with a non-targeted range of low dose ingredients (vitamins, minerals, herbs, amino acids). Vitamins have no targeted benefit.</p>

            <p>When you supplement the right ingredients to address the long-term implications of the natural variations in your DNA, you can treat and manage specific medical symptoms.</p>

            <p>Nutriceuticals are personalised nutrition medicines that make up for the long-term effects of gene variations in our DNA.</p>
          </div>

          <h3>The new you in a capsule</h3>

          <p>Medical nutritionists understand the therapeutic effects of vitamins and minerals at high dose.</p>

          <p>Based on your symptoms, lifestyle factors and family history, a nutriceutical can now be individually formulated for you by specially trained pharmacists.</p>
        </div>

        <div class="cols-1of2">
          <h3>Target medical conditions</h3>

          <h4>Inflammation</h4>

          <ul>
            <li>Migraine and chronic headache</li>
            <li>Chronic skeletal pain (e.g. back, shoulder, neck)</li>
            <li>Chronic joint pain</li>
            <li>Endometriosis</li>
            <li>Inflammatory bowel conditions</li>
            <li>Widespread pain (e.g. fibromyalgia)</li>
            <li>Nerve pain</li>
            <li>Vulvodynia (pelvic pain)</li>
            <li>Restless leg syndrome</li>
            <li>Arthritis</li>
            <li>Muscle cramps / muscle spasm</li>
          </ul>

          <h4>Mental Health</h4>

          <ul>
            <li>Stress</li>
            <li>Anxiety</li>
            <li>Depression</li>
          </ul>

          <h4>Energy / Cognition</h4>

          <ul>
            <li>Fatigue or lack of energy</li>
            <li>Insomnia (poor sleep)</li>
            <li>Brain fog (lack of concentration)</li>
          </ul>

          <h4>Cardiovascular</h4>

          <ul>
            <li>High blood pressure or stroke</li>
            <li>Diabetes</li>
          </ul>

          <h4>Specific</h4>

          <ul>
            <li>Osteoporosis</li>
            <li>Acne</li>
            <li>Menopause</li>
            <li>Infertility / conception issues</li>
            <li>Nerve disease (e.g. MS or Parkinson&rsquo;s disease)</li>
          </ul>

          <h4>Lifestyle &amp; Risk Behaviours</h4>

          <ul>
            <li>Lack of sleep</li>
            <li>Smoke cigarettes</li>
            <li>Work indoors</li>
            <li>Poor diet</li>
            <li>Overweight</li>
            <li>Frequent antibiotics</li>
            <li>Excessive alcohol consumption</li>
            <li>Frequent exposure to pollution</li>
            <li>Your age</li>
          </ul>
        </div>
      </div>
    </section>

    <section>
      <div class="container mx-auto row">
        <div class="cols-1of2">
          <h2>The Unique <span class="brand">My New Life</span> System</h2>

          <p>Based on your individual symptoms, family history and lifestyle factors, the My New Life system creates a personalised nutriceutical program tailored for each individual based on four key areas:</p>

          <ol>
            <li>Inflammation</li>
            <li>Cellular defense</li>
            <li>Vitamin D receptors</li>
            <li>Homocysteine</li>
          </ol>

          <h3>Inflammation</h3>

          <p>The body produces pro-inflammatory (increased inflammation) and anti-inflammatory (decreased inflammation) compounds called cytokines.</p>

          <p>We target inflammation-based medical conditions with specific nutrients to counteract the negative effects of specific gene variations to unwind and reverse chronic inflammation.</p>

          <h3>Cellular defense</h3>

          <p>Mitochondria within every living cell produce energy for life but they also produce reactive oxygen species (ROS) as a natural by-product of normal metabolism of oxygen. Elevated levels of ROS can damage your DNA, lipids and proteins. This is called oxidative stress and underlies all disease processes including cardiovascular disease, diabetes, cancer, neurological conditions and premature aging.</p>
        </div>

        <div class="cols-1of2">
          <p>We target medical conditions consistent with chronic oxidative stress with primary antioxidants to upregulate the bodies natural antioxidant enzymes.</p>

          <h3>Vitamin D receptors</h3>

          <p>Over 50% of the world&rsquo;s population are deficient in vitamin D due to dietary factors and their genetic inability to make the active form of vitamin D from the sun or absorbed from the intestine.</p>

          <p>Most people are aware of the link between vitamin D and calcium metabolism (e.g. bone health). However, most people aren&rsquo;t aware that vitamin D directly affects the function of over 150 genes and the production of essential hormones and neurotransmitters.</p>

          <p>As a consquence, vitamin D deficiency is a major contributor to chronic mental health disorders, immune system dysfunction and skin conditions.</p>

          <p>We target chronic medical conditions consistent with low Vitamin D utilisation with therapeutic doses of Vitamin D in each formulation</p>

          <h3>Homocysteine</h3>

          <p>Homocysteine is naturally produced within your body as a by-product of the breakdown of the essential amino acid methionine. Homocysteine is then broken down into important compounds such as antioxidants to reduce oxidative stress, melatonin for sleep, dopamine for mental function and adrenaline. However high levels of homocysteine contribute to cardiovascular disease, diabetes, Alzheimers disease, osteoporosis and low fertility.</p>

          <p>We target medical conditions and risk lifestyle factors with specific nutriceuticals to balance the long-term effects of elevated homocysteine.</p>
        </div>
      </div>
    </section>

    <section id="getting-started">
      <div class="container mx-auto row">
        <div class="cols-1of1">
          <h2>Getting Started</h2>
        </div>

        <div class="cols-1of2">
          <p>The My New Life system approach will assess your presenting symptoms as well as your lifestyle factors to achieve five key benefits:</p>

          <ol>
            <li>Reduce oxidative stress</li>
            <li>Supress pro-inflammatory mediators</li>
            <li>Enhance anti-inflammatory mediators</li>
            <li>Facilitate the balance of the honocysteine system</li>
            <li>Boost vitamin D to optimise the activity of vitamin D receptors</li>
          </ol>

          <p>Getting started is easy in five simple steps.</p>

          <h3>Step 1</h3>

          <p>Read the brochures and FAQs in the app provided.</p>

          <h3>Step 2</h3>

          <p>Complete and submit the obligation-free and confidential (encrypted) health assessment form online.</p>
        </div>

        <div class="cols-1of2">
          <h3>Step 3</h3>

          <p>The nutrition and pharmacist team will design a targeted nutriceutical that addresses the key nutrigenetic and nutrigenomic pathways to your wellbeing. They will email you the details of the package that is designed for you.</p>

          <h3>Step 4</h3>

          <p>Place your order.</p>

          <p>We accept payment by electronic fund transfer.</p>

          <h3>Step 5</h3>

          <p>Your order is dispatched by express courier within 24 hours. You can then start your new life by taking the prescribed nutriceutical on a daily basis.</p>

          <p>Your nutriceuticals can be reordered as needed or you can choose to have them sent to you every 90 days.</p>

          <p>Email our team any time with your questions.</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  export default {
    computed: {
      ...mapGetters({
        language: 'clientLanguage'
      })
    }
  }
</script>

<style></style>
